import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface ImportHistory {
  id: string;
  fileName: string;
  date: string;
  status: "completed" | "failed" | "processing";
  productsImported: number;
  errors: number;
}

const MOCK_HISTORY: ImportHistory[] = [
  { id: "1", fileName: "products_batch_001.csv", date: "2025-02-25 14:30", status: "completed", productsImported: 150, errors: 0 },
  { id: "2", fileName: "inventory_update.xlsx", date: "2025-02-20 09:15", status: "completed", productsImported: 45, errors: 3 },
  { id: "3", fileName: "new_products.csv", date: "2025-02-18 16:45", status: "failed", productsImported: 0, errors: 12 },
];

export default function ImportSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importStep, setImportStep] = useState<"upload" | "mapping" | "preview" | "complete">("upload");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
      setImportStep("mapping");
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setImportStep("mapping");
    }
  };

  const getStatusBadge = (status: ImportHistory["status"]) => {
    const styles = {
      completed: "bg-emerald-500/20 text-emerald-500",
      failed: "bg-red-500/20 text-red-500",
      processing: "bg-amber-500/20 text-amber-500",
    };
    return styles[status];
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posProducts.import.title")}</h1>
          <p className={themeClasses.textSecondary}>{t("posProducts.import.subtitle")}</p>
        </div>
        <button
          onClick={() => {/* Download template */}}
          className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium flex items-center gap-2`}
        >
          <i className="ri-download-line"></i>
          {t("posProducts.import.downloadTemplate")}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
            {importStep === "upload" && (
              <div
                className={`p-8 border-2 border-dashed rounded-xl m-4 transition-colors ${
                  dragActive ? "border-primary-500 bg-primary-500/10" : themeClasses.border
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-500/20 flex items-center justify-center">
                    <i className="ri-upload-cloud-2-line text-3xl text-primary-500"></i>
                  </div>
                  <h3 className={`font-semibold ${themeClasses.text} mb-2`}>
                    {t("posProducts.import.dragDrop")}
                  </h3>
                  <p className={`${themeClasses.textSecondary} mb-4`}>
                    {t("posProducts.import.supportedFormats")}
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-6 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium"
                  >
                    {t("posProducts.import.selectFile")}
                  </button>
                </div>
              </div>
            )}

            {importStep === "mapping" && selectedFile && (
              <div className="p-4">
                <div className={`flex items-center gap-3 p-4 rounded-xl ${themeClasses.cardBg} mb-4`}>
                  <i className="ri-file-excel-2-line text-2xl text-emerald-500"></i>
                  <div className="flex-1">
                    <p className={`font-medium ${themeClasses.text}`}>{selectedFile.name}</p>
                    <p className={`text-sm ${themeClasses.textSecondary}`}>
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                  <button
                    onClick={() => { setSelectedFile(null); setImportStep("upload"); }}
                    className="p-2 rounded-lg hover:bg-red-500/10 text-red-500"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>

                <h3 className={`font-semibold ${themeClasses.text} mb-4`}>
                  {t("posProducts.import.mapColumns")}
                </h3>

                <div className="space-y-3">
                  {["Product Name", "SKU", "Price", "Stock", "Category"].map((field) => (
                    <div key={field} className="flex items-center gap-4">
                      <span className={`w-32 ${themeClasses.text}`}>{field}</span>
                      <i className="ri-arrow-right-line text-primary-500"></i>
                      <select
                        className={`flex-1 px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.bg} ${themeClasses.text}`}
                      >
                        <option>{t("posProducts.import.selectColumn")}</option>
                        <option>Column A</option>
                        <option>Column B</option>
                        <option>Column C</option>
                      </select>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2 mt-6">
                  <button
                    onClick={() => setImportStep("upload")}
                    className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text}`}
                  >
                    {t("common.back")}
                  </button>
                  <button
                    onClick={() => setImportStep("preview")}
                    className="px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium"
                  >
                    {t("posProducts.import.preview")}
                  </button>
                </div>
              </div>
            )}

            {importStep === "preview" && (
              <div className="p-4">
                <div className={`p-4 rounded-xl ${themeClasses.cardBg} mb-4`}>
                  <div className="flex items-center gap-3">
                    <i className="ri-information-line text-blue-500 text-xl"></i>
                    <div>
                      <p className={`font-medium ${themeClasses.text}`}>
                        {t("posProducts.import.previewInfo")}
                      </p>
                      <p className={`text-sm ${themeClasses.textSecondary}`}>
                        150 {t("posProducts.import.productsFound")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className={`${themeClasses.cardBg}`}>
                      <tr>
                        <th className={`px-4 py-2 text-left text-sm ${themeClasses.textSecondary}`}>Name</th>
                        <th className={`px-4 py-2 text-left text-sm ${themeClasses.textSecondary}`}>SKU</th>
                        <th className={`px-4 py-2 text-right text-sm ${themeClasses.textSecondary}`}>Price</th>
                        <th className={`px-4 py-2 text-right text-sm ${themeClasses.textSecondary}`}>Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[1, 2, 3].map((i) => (
                        <tr key={i} className={`border-b ${themeClasses.border}`}>
                          <td className={`px-4 py-2 ${themeClasses.text}`}>Sample Product {i}</td>
                          <td className={`px-4 py-2 ${themeClasses.textSecondary}`}>PROD-00{i}</td>
                          <td className={`px-4 py-2 text-right ${themeClasses.text}`}>${(i * 29.99).toFixed(2)}</td>
                          <td className={`px-4 py-2 text-right ${themeClasses.text}`}>{i * 10}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex gap-2 mt-6">
                  <button
                    onClick={() => setImportStep("mapping")}
                    className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text}`}
                  >
                    {t("common.back")}
                  </button>
                  <button
                    onClick={() => setImportStep("complete")}
                    className="px-4 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium"
                  >
                    {t("posProducts.import.startImport")}
                  </button>
                </div>
              </div>
            )}

            {importStep === "complete" && (
              <div className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <i className="ri-check-line text-3xl text-emerald-500"></i>
                </div>
                <h3 className={`text-xl font-semibold ${themeClasses.text} mb-2`}>
                  {t("posProducts.import.importComplete")}
                </h3>
                <p className={`${themeClasses.textSecondary} mb-6`}>
                  150 {t("posProducts.import.productsImported")}
                </p>
                <button
                  onClick={() => { setSelectedFile(null); setImportStep("upload"); }}
                  className="px-6 py-2 rounded-xl bg-primary-500 text-gray-900 font-medium"
                >
                  {t("posProducts.import.importMore")}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4 h-fit`}>
          <h3 className={`font-semibold ${themeClasses.text} mb-4`}>{t("posProducts.import.history")}</h3>
          <div className="space-y-3">
            {MOCK_HISTORY.map((item) => (
              <div key={item.id} className={`p-3 rounded-xl ${themeClasses.cardBg}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <i className="ri-file-excel-2-line text-emerald-500"></i>
                    <span className={`text-sm font-medium ${themeClasses.text} truncate max-w-[150px]`}>
                      {item.fileName}
                    </span>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusBadge(item.status)}`}>
                    {item.status}
                  </span>
                </div>
                <div className={`text-xs ${themeClasses.textSecondary}`}>
                  <p>{item.date}</p>
                  <p>{item.productsImported} imported • {item.errors} errors</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
